import React, { useState, useRef, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { createStyles, makeStyles, CircularProgress } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Store from 'store/store';

import Charts from 'components/Charts';
import {
  PastPerformanceAggregatesType,
  PortfolioSplitTypeChartData,
  PolicyRiskScoreNamed,
  AggregateCostAndChargesTypes
} from 'store/modules/Charts/types';
import {
  ExistingPoliciesTable,
  RecommendedPoliciesTable
} from './PoliciesTable/PoliciesTable';
import { ExistingPolicyType } from 'store/modules/ExistingPolicies/types';
import { PolicyType } from 'store/modules/Policies/types';
import { BoxWrapper, Button } from 'components/Base';
import { RiskGroupType } from 'store/modules/Firm/types';
import PastPerformance from './PastPerformance';
import ScatterChart from 'components/InvestmentAnalyser/ScatterChart';
import CumulativePerformanceTable from './PastPerformanceTable/CumulativePerformanceTable';
import DiscretePerformanceTable from './PastPerformanceTable/DiscretePerformanceTable';
import AnnualisedPerformanceTable from './PastPerformanceTable/AnnualisedPerformanceTable';
import CalendarPerformanceTable from './PastPerformanceTable/CalendarPerformanceTable';
import RiskDescriptionModal from 'components/RiskDescription/RiskDescriptionModal';
import ChargesWrapper from 'components/Policies/Charges/ChargesWrapper';
import { combinePolicyRiskScores } from 'components/Policies/utils';
import MaxDrawdownTable from './PastPerformanceTable/MaxDrawdownTable';
import BreakDownWrapper from 'components/Charts/Utils/Breakdown/BreakDownWrapper';
import Router from 'components/Router/Router';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolderOutlined';
import { MetaType } from 'store/modules/Meta/types';
import { getComfortRanges } from 'components/Charts/Utils/MorningstarRiskProfiler/utils';
import { comfortRangeConfig } from 'components/RiskMapping/utils';
import MorningstarRiskTolerance from 'components/RiskAnalysis/Morningstar/MorningstarRiskTolerance';
import MorningstarTargetAllocation from 'components/RiskAnalysis/Morningstar/MorningstarTargetAllocation';

const { connect, selectors } = Store;
const { useHistory } = Router;

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      flexGrow: 1
    },
    gridBox: {
      minHeight: 433,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    gridBoxBig: {
      minHeight: 500
    },
    riskDescription: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: 'calc(100% - 4.125rem)',
      color: '#fff'
    },
    btnBox: {
      marginTop: '2rem'
    },
    chartBox: {
      height: '100%',
      padding: '1rem'
    },
    tableTitle: {
      padding: '0 1rem',
      marginBottom: '1.5rem'
    },
    descriptionWrapper: {
      display: 'flex',
      flex: 1,
      alignItems: 'center'
    },
    legend: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    legendSingle: {
      display: 'flex',
      alignItems: 'center',
      marginRight: '1rem'
    },
    legendBox: {
      width: '10px',
      minWidth: '10px',
      height: '10px',
      marginRight: '0.5rem',
      borderRadius: '50%'
    },
    pastPermanceDate: {
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-end',
      paddingRight: '0.5rem'
    },
    tooltip: {
      margin: 0
    },
    loaderWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      flex: 1
    },
    text: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
      marginBottom: '1rem',

      '& strong': {
        marginRight: '2px'
      }
    },
    dot: {
      display: 'inline-block',
      width: '5px',
      height: '5px',
      margin: '0 5px',
      borderRadius: '50%',
      backgroundColor: 'black'
    },
    textRow: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start'
    }
  })
);

interface InvestmentPolicySummaryProps {
  pastPerformanceAggregate: PastPerformanceAggregatesType;
  existingPolicies: Array<ExistingPolicyType>;
  recommendedPolicies: Array<PolicyType>;
  portfolioSplit: Array<PortfolioSplitTypeChartData>;
  portfolioRiskScore: Array<PolicyRiskScoreNamed>;
  riskSummaries: any;
  forecastGrossInvestmentReturn: any;
  aggregateCostAndCharges: AggregateCostAndChargesTypes[];
  hasExistingPolicies: boolean;
  firmRiskGroup: RiskGroupType;
  isPastPerformanceLoading: boolean;
  totalPerformanceData: any;
  existingPoliciesRiskScore: any;
  recommendedPoliciesRiskScore: any;
  portfolio: string;
  benchmarks: Array<MetaType>;
  caseInfo: any;
  recommendedRiskAnalysis: any;
  riskProfiles: any;
}

const IPS = ({
  pastPerformanceAggregate,
  existingPolicies,
  recommendedPolicies,
  portfolioSplit,
  riskSummaries,
  forecastGrossInvestmentReturn,
  portfolioRiskScore,
  hasExistingPolicies,
  firmRiskGroup,
  isPastPerformanceLoading,
  totalPerformanceData,
  existingPoliciesRiskScore,
  recommendedPoliciesRiskScore,
  portfolio,
  benchmarks,
  caseInfo,
  recommendedRiskAnalysis,
  riskProfiles
}: InvestmentPolicySummaryProps) => {
  const classes = useStyles();
  const history = useHistory();
  const isEvalue = caseInfo.riskProfiler === 'EValue';

  const pastPerformanceChart = useRef(null);

  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);

  const isPastPerformanceData =
    recommendedPolicies.length > 0 || existingPolicies.length > 0;

  const existingRiskScore = portfolioRiskScore.filter(
    (policy: any) => policy.name === 'Existing'
  )[0]?.risk_score;

  const recommendedRiskScore = portfolioRiskScore.filter(
    (policy: any) => policy.name === 'Recommended'
  )[0]?.risk_score;

  const isFullWidthCharts = portfolioSplit.length > 5;

  const modifiedData = combinePolicyRiskScores(
    existingPoliciesRiskScore,
    recommendedPoliciesRiskScore
  );

  const [comfortRanges, setComfortRanges] = useState([] as any);

  useEffect(() => {
    const ranges = getComfortRanges(caseInfo.riskProfiler, comfortRangeConfig, [
      recommendedRiskAnalysis['comfort_ranges']
    ]);
    setComfortRanges(ranges);
  }, [caseInfo.riskProfiler, comfortRangeConfig]);

  return (
    <>
      {recommendedPolicies.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            marginBottom: '1rem',
            padding: '2rem'
          }}
        >
          <CreateNewFolderIcon
            htmlColor="#6b7280"
            style={{ width: '36px', height: '36px' }}
          />
          <div style={{ fontWeight: 600 }}>No Recommended Policies</div>
          <div style={{ color: '#6b7280' }}>
            To see a summary please create a recommended policy.
          </div>
          <div
            style={{
              padding: '1rem 0rem 0rem'
            }}
          >
            <Button
              onClick={() => history.push(`/${portfolio}/recommendations`)}
            >
              View Recommendations
            </Button>
          </div>
        </div>
      ) : (
        <Grid container direction="row" className={classes.root} spacing={2}>
          {caseInfo.riskProfiler !== 'EValue' && !!comfortRanges ? (
            <>
              <Grid item xs={12} md={6}>
                <MorningstarRiskTolerance />
              </Grid>

              <Grid item xs={12} md={6}>
                <MorningstarTargetAllocation
                  score={riskProfiles?.[0]?.recommended_risk_profile || 0}
                />
              </Grid>

              <Grid item xs={12} md={12} style={{ minHeight: '27rem' }}>
                <BoxWrapper title="Risk Analysis">
                  <div style={{ padding: '0 5rem' }}>
                    <Charts.MorningstarRiskProfiler.PolicyAnalysis
                      ranges={comfortRanges}
                      markers={[
                        {
                          position: 45,
                          type: 'dotted',
                          label: 'Current Position'
                        },
                        { position: 75, type: 'solid', label: 'High Threshold' }
                      ]}
                    />
                  </div>
                </BoxWrapper>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <BoxWrapper
                  title="Risk"
                  className={classes.gridBox}
                  info=""
                  headerChildren={
                    recommendedRiskScore ? (
                      <Button
                        type="button"
                        onClick={() => setIsRiskModalOpen(true)}
                      >
                        View Risk
                      </Button>
                    ) : (
                      <></>
                    )
                  }
                >
                  <div
                    style={{
                      flex: '1',
                      display: 'flex',
                      flexDirection: 'column',
                      marginBottom: '1rem'
                    }}
                  >
                    <Charts.IPS.RiskScore
                      data={portfolioRiskScore}
                      hasExistingPolicies={hasExistingPolicies}
                      riskGroup={firmRiskGroup}
                    />
                  </div>
                </BoxWrapper>
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <BoxWrapper
                  title="Policy risk score"
                  className={classes.gridBox}
                >
                  <Charts.ExistingPolicyAnalysis.PoliciesRiskScore
                    data={modifiedData}
                    riskGroup={firmRiskGroup}
                  />
                </BoxWrapper>
              </Grid>

              {recommendedRiskScore ? (
                <RiskDescriptionModal
                  score={recommendedRiskScore}
                  isOpen={isRiskModalOpen}
                  setIsOpen={setIsRiskModalOpen}
                />
              ) : (
                <></>
              )}

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <BoxWrapper title="Risk summary" className={classes.gridBox}>
                  <Grid item xs={12} className={classes.chartBox}>
                    <Charts.IPS.RiskSummary data={riskSummaries} />
                  </Grid>
                </BoxWrapper>
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <BoxWrapper
                  title="Forecast gross investment returns p.a."
                  className={classes.gridBox}
                  tooltip=""
                  helperText={
                    <>
                      <p>
                        The 'In a very good year' and 'In a very bad year'
                        denote a 90% confidence level. The 'In a good year' and
                        'In a bad year' denote a 50% confidence level.
                      </p>
                      <p>
                        These are estimates based on historical data and expert
                        opinion from EValue about future long-term possible
                        asset class returns and are not guaranteed - the returns
                        in any year could be outside this range.
                      </p>
                    </>
                  }
                >
                  <Grid item xs={12} className={classes.chartBox}>
                    <Charts.IPS.ForecastGrossInvReturns
                      data={forecastGrossInvestmentReturn}
                    />
                  </Grid>
                </BoxWrapper>
              </Grid>
            </>
          )}

          {hasExistingPolicies && (
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <BoxWrapper title="Existing portfolio">
                <ExistingPoliciesTable
                  policies={existingPolicies}
                  isEvalue={isEvalue}
                  riskScore={existingRiskScore}
                />
              </BoxWrapper>
            </Grid>
          )}

          <Grid item xs={12} sm={12} md={12} lg={12}>
            <BoxWrapper title="Recommended portfolio">
              <RecommendedPoliciesTable
                policies={recommendedPolicies}
                isEvalue={isEvalue}
                riskScore={recommendedRiskScore}
              />
            </BoxWrapper>
          </Grid>

          {isEvalue && (
            <Grid item xs={12}>
              <BoxWrapper
                title="Portfolio split"
                className={classes.gridBoxBig}
                helperText="Where regular contributions are being recommended, the policy value includes the annual sum of contributions payable."
                style={{ height: '100%' }}
              >
                <Grid item xs={12} className={classes.chartBox}>
                  <Charts.IPS.PortfolioSplit data={portfolioSplit} />
                </Grid>
              </BoxWrapper>
            </Grid>
          )}

          <Grid item xs={12}>
            <BoxWrapper
              title="Past performance"
              className={classes.gridBoxBig}
              info={pastPerformanceAggregate.date_range}
              helperText={
                <Typography variant="subtitle2" className={classes.text}>
                  <div>
                    Performance is based on the current fund allocation and
                    doesn't take into account any previous fund switches.
                  </div>
                  <div className={classes.textRow}>
                    <strong>Performance basis: </strong> Total cumulative return
                  </div>
                  <div className={classes.textRow}>
                    <strong>Pricing Spread: </strong> Bid-Bid
                  </div>
                  <div className={classes.textRow}>
                    <strong>Data Frequency: </strong> Daily
                  </div>
                  <div className={classes.textRow}>
                    <strong>Currency: </strong> Pounds Sterling
                  </div>
                  <p>
                    Where regular contributions are being recommended, past
                    performance is calculated based on the annual sum of
                    contributions payable.
                  </p>
                </Typography>
              }
            >
              {isPastPerformanceLoading ? (
                <div className={classes.loaderWrapper}>
                  <CircularProgress size="80px" />
                </div>
              ) : (
                <Grid item xs={12} ref={pastPerformanceChart}>
                  <PastPerformance
                    data={pastPerformanceAggregate.data}
                    errors={pastPerformanceAggregate.failed_codes}
                    limits={pastPerformanceAggregate.limits}
                    earliestDates={pastPerformanceAggregate.earliest_dates}
                    footprints={pastPerformanceAggregate.footprints}
                    height={isFullWidthCharts ? 360 : 400}
                    isAnyPolicyAdded={isPastPerformanceData}
                    hasDateControl={false}
                  />
                </Grid>
              )}
            </BoxWrapper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <BoxWrapper title="Cumulative Performance">
              {isPastPerformanceLoading ? (
                <div className={classes.loaderWrapper}>
                  <CircularProgress size="80px" />
                </div>
              ) : (
                <CumulativePerformanceTable
                  data={pastPerformanceAggregate.tabular_data}
                  benchmarks={benchmarks}
                />
              )}
            </BoxWrapper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <BoxWrapper title="Discrete Performance">
              {isPastPerformanceLoading ? (
                <div className={classes.loaderWrapper}>
                  <CircularProgress size="80px" />
                </div>
              ) : (
                <DiscretePerformanceTable
                  data={pastPerformanceAggregate.tabular_data}
                  benchmarks={benchmarks}
                />
              )}
            </BoxWrapper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <BoxWrapper title="Annualised Performance">
              {isPastPerformanceLoading ? (
                <div className={classes.loaderWrapper}>
                  <CircularProgress size="80px" />
                </div>
              ) : (
                <AnnualisedPerformanceTable
                  data={pastPerformanceAggregate.tabular_data}
                  benchmarks={benchmarks}
                />
              )}
            </BoxWrapper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <BoxWrapper title="Calendar Performance">
              {isPastPerformanceLoading ? (
                <div className={classes.loaderWrapper}>
                  <CircularProgress size="80px" />
                </div>
              ) : (
                <CalendarPerformanceTable
                  data={pastPerformanceAggregate.tabular_data}
                  benchmarks={benchmarks}
                />
              )}
            </BoxWrapper>
          </Grid>

          <Grid item xs={12} sm={12}>
            <BoxWrapper title="Max Drawdown">
              {isPastPerformanceLoading ? (
                <div className={classes.loaderWrapper}>
                  <CircularProgress size="80px" />
                </div>
              ) : (
                <MaxDrawdownTable benchmarks={benchmarks} />
              )}
            </BoxWrapper>
          </Grid>

          <Grid item xs={12}>
            <BreakDownWrapper />
          </Grid>

          <Grid
            container
            spacing={2}
            style={{ display: 'flex', alignItems: 'stretch', padding: '8px' }}
          >
            <Grid item xs={12}>
              <BoxWrapper title="Scatter Chart" className={classes.gridBoxBig}>
                <Grid item xs={12} className={classes.chartBox}>
                  {isPastPerformanceLoading ? (
                    <div className={classes.loaderWrapper}>
                      <CircularProgress size="80px" />
                    </div>
                  ) : (
                    <Grid item xs={12} className={classes.chartBox}>
                      <ScatterChart
                        data={totalPerformanceData}
                        performanceData={pastPerformanceAggregate.tabular_data}
                        height={250}
                        hasDateControl={false}
                      />
                    </Grid>
                  )}
                </Grid>
              </BoxWrapper>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <ChargesWrapper />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default connect((state: any) => ({
  pastPerformanceAggregate:
    selectors.caseSelectors.pastPerformanceAggregates(state),
  totalPerformanceData: selectors.caseSelectors.totalPerformanceData(state),
  existingPolicies: selectors.caseSelectors.existingPolicies(state),
  recommendedPolicies: selectors.caseSelectors.policies(state),
  portfolioSplit: selectors.caseSelectors.portfolioSplit(state),
  riskSummaries: selectors.IPSSelectos.riskSummary(state),
  forecastGrossInvestmentReturn:
    selectors.IPSSelectos.forecastGrossInvestmentReturn(state),
  portfolioRiskScore: selectors.caseSelectors.portfolioRiskScore(state),
  aggregateCostAndCharges:
    selectors.caseSelectors.aggregateCostAndCharges(state),
  hasExistingPolicies: selectors.caseSelectors.hasExistingPolicies(state),
  firmRiskGroup: selectors.firmSelectors.riskGroup(state),
  existingPoliciesRiskScore:
    selectors.caseSelectors.existingPoliciesRiskScore(state),
  recommendedPoliciesRiskScore:
    selectors.caseSelectors.recommendedPoliciesRiskScore(state),
  benchmarks: selectors.metaSelectors.benchmarks(state),
  caseInfo: selectors.caseSelectors.case(state),
  recommendedRiskAnalysis:
    selectors.caseSelectors.recommendedRiskAnalysis(state),
  riskProfiles: selectors.caseSelectors.getRiskSummary(state)
}))(IPS);
